import {RsnMessage, useBoolean, useLatestCallback} from '@raison/ui-kit';
import {
    DataTag,
    InfiniteData,
    QueryClient,
    QueryKey,
    useQueryClient,
    useSuspenseInfiniteQuery,
} from '@tanstack/react-query';
import React, {Fragment, useEffect} from 'react';
import {useTransactionsHistoryOptions} from '../api/useTransactionsHistoryOptions';
import {PendingTransactModal} from './PendingTransactModal/PendingTransactModal';

export function PendingTransactionsToast() {
    const [isOpen, setIsOpen] = useBoolean(false);

    const options = useTransactionsHistoryOptions({status: ['pending']});
    const qc = useQueryClient();
    useAfterUnmount(() => {
        removeAllButFirst(qc, options.queryKey);
    });

    const {
        data: {orders = [], total = 0} = {},
        isLoading,
        fetchNextPage,
    } = useSuspenseInfiniteQuery({...options, refetchOnMount: true});

    const isListExhausted = orders.length === total;

    return (
        <Fragment>
            {!!total && (
                <Fragment>
                    <RsnMessage.Progress onClick={setIsOpen.on}>
                        Transactions in progress <span className='float-end'>({total})</span>
                    </RsnMessage.Progress>
                </Fragment>
            )}
            <PendingTransactModal
                orders={orders}
                fetchNextPage={fetchNextPage}
                loading={isLoading}
                isOpen={isOpen}
                onClose={setIsOpen.off}
                isListExhausted={isListExhausted}
            />
        </Fragment>
    );
}

function useAfterUnmount(callback: () => void) {
    callback = useLatestCallback(callback);
    useEffect(() => {
        return () => {
            setTimeout(callback);
        };
    }, []);
}

function removeAllButFirst(qc: QueryClient, queryKey: DataTag<QueryKey, InfiniteData<unknown>, Error>) {
    const query = qc.getQueryCache().find({queryKey});
    const isNotObserved = !query || query.getObserversCount() === 0;
    if (isNotObserved) {
        qc.setQueryData(queryKey, prev => {
            return prev && {pages: [prev.pages[0]], pageParams: [prev.pageParams[0]]};
        });
    }
}
